import type { DraftState, KnownPairSummary, PrimerEstimate, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { isArchiveOrder } from './archive.ts'

type Side = 'first' | 'second'

export type WrittenOrientation = {
  /** The pair goes into the order with the primer R2 starts with as "forward". */
  swapped: boolean
  /** Why this orientation: the lab's earlier orders, the registered pair, or the read start. */
  basis: 'earlier-orders' | 'registry' | 'reads'
}

const READ_START: WrittenOrientation = { swapped: false, basis: 'reads' }

const sideOf = (pair: KnownPairSummary, gene: string | null): Side | undefined => {
  if (!gene) {
    return undefined
  }
  if (pair.firstVariants.includes(gene) || gene === pair.first) {
    return 'first'
  }
  return pair.secondVariants.includes(gene) || gene === pair.second ? 'second' : undefined
}

/** The side of the pair R1 opens with, as the estimate read it. */
const readStartSide = (pair: KnownPairSummary, estimate: PrimerEstimate): Side | undefined => {
  const forward = sideOf(pair, estimate.forward.gene)
  if (forward) {
    return forward
  }
  const reverse = sideOf(pair, estimate.reverse.gene)
  return reverse && (reverse === 'first' ? 'second' : 'first')
}

/** The side the registration spelled like the estimate puts forward, else the pair's leading registration. */
const registeredForward = (pair: KnownPairSummary, estimate: PrimerEstimate): Side | undefined => {
  const genes = [estimate.forward.gene, estimate.reverse.gene]
  const registrations = pair.registrations ?? []
  const registration =
    registrations.find((candidate) => genes.includes(candidate.forward) && genes.includes(candidate.reverse)) ?? registrations[0]
  return registration && sideOf(pair, registration.forward)
}

/**
 * How a group's estimated primers go into the order's forward and reverse fields. A decomposed pool is
 * demultiplexed by `^forward` on R1, so it keeps the read start. Everywhere else the pipeline orients the
 * reads from the forward to the reverse field, so a swap would reverse-complement every OTU against the
 * lab's earlier orders: the lab's orientation wins, then the registered one, then the read start.
 */
export const writtenOrientation = (draft: DraftState, group: WizardSampleGroup): WrittenOrientation => {
  const primers = draft.discovery?.primers
  const estimate = group.estimate
  if (isArchiveOrder(draft) || primers?.status !== 'found' || !estimate) {
    return READ_START
  }
  const readStart = readStartSide(primers.pair, estimate)
  if (!readStart) {
    return READ_START
  }
  const lab = primers.pair.labForward
  if (lab) {
    return { swapped: lab !== readStart, basis: 'earlier-orders' }
  }
  const registered = registeredForward(primers.pair, estimate)
  return registered ? { swapped: registered !== readStart, basis: 'registry' } : READ_START
}

/** A group's estimate with its sides as they go into the order's forward and reverse fields. */
export const writtenEstimate = (draft: DraftState, group: WizardSampleGroup): PrimerEstimate | undefined => {
  const estimate = group.estimate
  return estimate && writtenOrientation(draft, group).swapped ? { forward: estimate.reverse, reverse: estimate.forward } : estimate
}
